import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@nextui-org/react';
import {
  ArrowLeft,
  Award,
  Search,
  BarChart3,
  Check,
  ChevronLeft,
  Clock,
  Crown,
  Gamepad2,
  Gift,
  ListChecks,
  LogIn,
  Star,
  Target,
  Timer,
  TimerOff,
  Trophy,
  Users,
  X,
} from 'lucide-react';
import predictionService, {
  GameDetailResponse,
  LeaderboardEntry,
  MatchPrediction,
  PredictionFixture,
  PredictionGame,
  PredictionLeaderboard,
  PredictionParticipant,
  SpecialDeadlineInfo,
  TournamentPlayer,
  MyStats,
} from '../services/predictionService';
import { useAuth } from '../contexts/AuthContext';

const ACCENT = '#3fced1';
const ACCENT_BG = 'rgba(63, 206, 209, 0.1)';
const ACCENT_BORDER = 'rgba(63, 206, 209, 0.3)';

type TabKey = 'tippen' | 'spezial' | 'meine' | 'rangliste' | 'sponsoren';

interface Feedback {
  type: 'success' | 'error';
  text: string;
}

// ---------- Kleine Hilfskomponenten ----------

const PlayerAvatar: React.FC<{ name?: string | null; image?: string | null; size?: number }> = ({
  name,
  image,
  size = 40,
}) => {
  const [imgError, setImgError] = useState(false);
  const initials = predictionService.getInitials(name);

  if (image && !imgError) {
    return (
      <img
        src={image}
        alt={predictionService.cleanPlayerName(name) || 'Spieler'}
        width={size}
        height={size}
        onError={() => setImgError(true)}
        className="rounded-full object-cover bg-zinc-800 flex-shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-zinc-800 flex items-center justify-center text-gray-300 font-bold flex-shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {initials}
    </div>
  );
};

// ---------- Page ----------

const PredictionGameDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useAuth();

  // Game / Teilnahme
  const [isLoadingGame, setIsLoadingGame] = useState(true);
  const [detail, setDetail] = useState<GameDetailResponse | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Events (Fixtures + Results gemischt, wie Flutter)
  const [events, setEvents] = useState<PredictionFixture[]>([]);
  const [players, setPlayers] = useState<TournamentPlayer[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);

  // Spezial-Tipps
  const [specialDeadline, setSpecialDeadline] = useState<SpecialDeadlineInfo | null>(null);
  const [selectedSemiFinalists, setSelectedSemiFinalists] = useState<string[]>([]);
  const [selectedChampion, setSelectedChampion] = useState<string | null>(null);
  const [isSubmittingSemiFinal, setIsSubmittingSemiFinal] = useState(false);
  const [isSubmittingChampion, setIsSubmittingChampion] = useState(false);
  const [semiPlayerSearch, setSemiPlayerSearch] = useState('');
  const [championPlayerSearch, setChampionPlayerSearch] = useState('');

  // Rangliste
  const [leaderboard, setLeaderboard] = useState<PredictionLeaderboard | null>(null);
  const [leaderboardEntries, setLeaderboardEntries] = useState<LeaderboardEntry[]>([]);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(true);
  const [isLoadingMoreLeaderboard, setIsLoadingMoreLeaderboard] = useState(false);

  // Meine Stats
  const [myStats, setMyStats] = useState<MyStats | null>(null);

  // Registrierung
  const [registerUsername, setRegisterUsername] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);

  // Tipp-Modal
  const [predictionModalFixture, setPredictionModalFixture] = useState<PredictionFixture | null>(null);
  const [modalHomeScore, setModalHomeScore] = useState('');
  const [modalAwayScore, setModalAwayScore] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);
  const [isSubmittingPrediction, setIsSubmittingPrediction] = useState(false);

  // UI
  const [activeTab, setActiveTab] = useState<TabKey>('tippen');
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const game: PredictionGame | null = detail?.game ?? null;
  const participation: PredictionParticipant | null = detail?.participation ?? null;
  const isRegistered = detail?.isRegistered === true;

  // Aktive Sponsoren (sortiert; bei randomizeSponsors einmalig gemischt) —
  // MUSS vor allen Early-Returns stehen (Rules of Hooks)
  const activeSponsors = useMemo(() => {
    const list = (game?.sponsors ?? [])
      .filter((s) => s && s.active !== false)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    if (game?.randomizeSponsors) {
      for (let i = list.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
      }
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.sponsors, game?.randomizeSponsors]);

  const showFeedback = useCallback((type: Feedback['type'], text: string) => {
    setFeedback({ type, text });
    window.setTimeout(() => setFeedback(null), 5000);
  }, []);

  // ---------- Daten laden ----------

  const loadGame = useCallback(async () => {
    if (!id) return;
    setIsLoadingGame(true);
    setNotFound(false);
    const result = await predictionService.getGameById(id);
    if (!result || !result.game) {
      setNotFound(true);
      setDetail(null);
    } else {
      setDetail(result);
    }
    setIsLoadingGame(false);
  }, [id]);

  const loadEvents = useCallback(async () => {
    if (!id) return;
    setIsLoadingEvents(true);
    const [fixturesData, results, deadlineData] = await Promise.all([
      // all=true: ALLE Fixture-Seiten laden, nicht nur die nächsten Spieltage
      predictionService.getTournamentFixtures(id, { all: true }),
      predictionService.getTournamentResults(id),
      predictionService.getSpecialPredictionsDeadline(id),
    ]);

    const merged: PredictionFixture[] = [];
    const seen = new Set<string>();
    [...(fixturesData?.fixtures ?? []), ...(results ?? [])].forEach((event) => {
      const key = event?._id || event?.flashliveEventId || '';
      if (key && seen.has(key)) return;
      if (key) seen.add(key);
      merged.push(event);
    });
    merged.sort((a, b) => {
      const ta = predictionService.parseDate(a.startTime)?.getTime() ?? 0;
      const tb = predictionService.parseDate(b.startTime)?.getTime() ?? 0;
      return ta - tb;
    });

    setEvents(merged);

    // Spieler-Pool für Spezial-Tipps: fixtures.players PLUS alle Teilnehmer
    // aus Fixtures UND Ergebnissen (vorher fehlten Spieler, deren Spiele
    // nicht im geladenen Fixture-Fenster lagen).
    const pool = new Map<string, { id: string | null; name: string; image: string | null }>();
    (fixturesData?.players ?? []).forEach((p) => {
      const n = predictionService.normalizePlayerInfo(p);
      if (n.id) pool.set(n.id, n);
    });
    merged.forEach((event) => {
      [event.homeParticipant, event.awayParticipant].forEach((part) => {
        if (part?.id && !pool.has(part.id)) {
          pool.set(part.id, { id: part.id, name: part.name ?? '', image: part.image ?? null });
        }
      });
    });
    setPlayers(
      Array.from(pool.values()).sort((a, b) =>
        predictionService.cleanPlayerName(a.name).localeCompare(predictionService.cleanPlayerName(b.name), 'de')
      )
    );

    setSpecialDeadline(deadlineData);
    setIsLoadingEvents(false);
  }, [id]);

  const loadLeaderboard = useCallback(async () => {
    if (!id) return;
    setIsLoadingLeaderboard(true);
    const data = await predictionService.getLeaderboard(id, 1, 20);
    setLeaderboard(data);
    setLeaderboardEntries(data?.rankings ?? []);
    setIsLoadingLeaderboard(false);
  }, [id]);

  const loadMyStats = useCallback(async () => {
    if (!id || !isAuthenticated) return;
    const stats = await predictionService.getMyStats(id);
    setMyStats(stats);
  }, [id, isAuthenticated]);

  useEffect(() => {
    loadGame();
    loadEvents();
    loadLeaderboard();
  }, [loadGame, loadEvents, loadLeaderboard]);

  useEffect(() => {
    if (isRegistered) loadMyStats();
  }, [isRegistered, loadMyStats]);

  // Spezial-Tipps aus bestehender Teilnahme initialisieren
  useEffect(() => {
    if (!participation) return;
    const semiPlayers = participation.specialPredictions?.semiFinal?.players ?? [];
    const ids = semiPlayers
      .map((p) => predictionService.normalizePlayerInfo(p).id)
      .filter((pid): pid is string => Boolean(pid));
    if (ids.length > 0) setSelectedSemiFinalists(ids);

    const championId = participation.specialPredictions?.champion?.playerId;
    if (championId) setSelectedChampion(championId);
  }, [participation]);

  // Default-Tab: nicht registriert → Rangliste
  useEffect(() => {
    if (!isLoadingGame && !isRegistered) setActiveTab('rangliste');
  }, [isLoadingGame, isRegistered]);

  // ---------- Abgeleitete Daten ----------

  const settings = game?.settings ?? null;
  const semiFinalEnabled = settings?.specialPredictions?.semiFinalEnabled === true;
  const championEnabled = settings?.specialPredictions?.championEnabled === true;
  const hasSpecialPredictions = semiFinalEnabled || championEnabled;
  const canPredictSpecial = specialDeadline?.canPredict !== false;

  const eventsByDate = useMemo(() => {
    const map = new Map<string, PredictionFixture[]>();
    events.forEach((event) => {
      const key = predictionService.getDateKey(event.startTime);
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(event);
    });
    return map;
  }, [events]);

  const sortedDateKeys = useMemo(() => Array.from(eventsByDate.keys()).sort(), [eventsByDate]);

  // Initiale Datums-Auswahl: erster Tag >= heute, sonst letzter Tag
  useEffect(() => {
    if (sortedDateKeys.length === 0 || selectedDateKey) return;
    const todayKey = predictionService.getDateKey(new Date().toISOString());
    const found = sortedDateKeys.find((key) => key >= todayKey);
    setSelectedDateKey(found ?? sortedDateKeys[sortedDateKeys.length - 1]);
  }, [sortedDateKeys, selectedDateKey]);

  const visibleEvents = useMemo(() => {
    if (!selectedDateKey) return [];
    return eventsByDate.get(selectedDateKey) ?? [];
  }, [eventsByDate, selectedDateKey]);

  const myPredictionForEvent = useCallback(
    (event: PredictionFixture): MatchPrediction | null => {
      const predictions = participation?.matchPredictions ?? [];
      return (
        predictions.find((p) => {
          const eventId = predictionService.getPredictionEventId(p);
          return (
            (eventId && event._id && eventId === event._id) ||
            (p.flashliveEventId && event.flashliveEventId && p.flashliveEventId === event.flashliveEventId)
          );
        }) ?? null
      );
    },
    [participation]
  );

  // ---------- Aktionen ----------

  const handleRegister = async () => {
    if (!id) return;
    setRegisterError(null);

    const username = registerUsername.trim();
    if (username.length < 2 || username.length > 30) {
      setRegisterError('Der Benutzername muss zwischen 2 und 30 Zeichen lang sein');
      return;
    }
    if (game?.termsUrl && !acceptedTerms) {
      setRegisterError('Bitte akzeptiere die Tippspielbedingungen');
      return;
    }

    setIsRegistering(true);
    try {
      await predictionService.registerForGame(id, username);
      showFeedback('success', 'Erfolgreich registriert!');
      await loadGame();
      await loadLeaderboard();
      setActiveTab('tippen');
    } catch (error: any) {
      setRegisterError(error?.message || 'Registrierung fehlgeschlagen');
    } finally {
      setIsRegistering(false);
    }
  };

  const openPredictionModal = (event: PredictionFixture) => {
    const existing = myPredictionForEvent(event);
    setModalHomeScore(
      existing?.predictedScore?.home !== null && existing?.predictedScore?.home !== undefined
        ? String(existing.predictedScore.home)
        : ''
    );
    setModalAwayScore(
      existing?.predictedScore?.away !== null && existing?.predictedScore?.away !== undefined
        ? String(existing.predictedScore.away)
        : ''
    );
    setModalError(null);
    setPredictionModalFixture(event);
  };

  const handleSubmitPrediction = async () => {
    if (!id || !predictionModalFixture?._id) return;
    setModalError(null);

    const home = parseInt(modalHomeScore, 10);
    const away = parseInt(modalAwayScore, 10);
    // Modus-Validierung: z.B. Best of 11 → Sieger braucht genau 6 Legs
    const validationError = predictionService.validatePredictedScore(predictionModalFixture, home, away);
    if (validationError) {
      setModalError(validationError);
      return;
    }
    const predictedWinner = home > away ? 1 : 2;

    setIsSubmittingPrediction(true);
    try {
      const participant = await predictionService.submitMatchPrediction(
        id,
        predictionModalFixture._id,
        predictedWinner,
        { home, away }
      );
      setDetail((prev) => (prev ? { ...prev, participation: participant } : prev));
      setPredictionModalFixture(null);
      showFeedback('success', 'Tipp erfolgreich gespeichert!');
      loadMyStats();
    } catch (error: any) {
      setModalError(error?.message || 'Tipp konnte nicht gespeichert werden');
    } finally {
      setIsSubmittingPrediction(false);
    }
  };

  const toggleSemiFinalist = (playerId: string) => {
    setSelectedSemiFinalists((prev) => {
      if (prev.includes(playerId)) return prev.filter((p) => p !== playerId);
      if (prev.length >= 4) return prev; // max 4
      return [...prev, playerId];
    });
  };

  const handleSubmitSemiFinal = async () => {
    if (!id || selectedSemiFinalists.length !== 4) return;
    setIsSubmittingSemiFinal(true);
    try {
      const participant = await predictionService.submitSemiFinalPrediction(id, selectedSemiFinalists);
      setDetail((prev) => (prev ? { ...prev, participation: participant } : prev));
      showFeedback('success', 'Halbfinale-Tipp erfolgreich gespeichert!');
    } catch (error: any) {
      showFeedback('error', error?.message || 'Halbfinale-Tipp konnte nicht gespeichert werden');
    } finally {
      setIsSubmittingSemiFinal(false);
    }
  };

  const handleSubmitChampion = async () => {
    if (!id || !selectedChampion) return;
    setIsSubmittingChampion(true);
    try {
      const participant = await predictionService.submitChampionPrediction(id, selectedChampion);
      setDetail((prev) => (prev ? { ...prev, participation: participant } : prev));
      showFeedback('success', 'Sieger-Tipp erfolgreich gespeichert!');
    } catch (error: any) {
      showFeedback('error', error?.message || 'Sieger-Tipp konnte nicht gespeichert werden');
    } finally {
      setIsSubmittingChampion(false);
    }
  };

  const loadMoreLeaderboard = async () => {
    if (!id || !leaderboard || isLoadingMoreLeaderboard) return;
    if (leaderboard.page >= leaderboard.totalPages) return;
    setIsLoadingMoreLeaderboard(true);
    const next = await predictionService.getLeaderboard(id, leaderboard.page + 1, leaderboard.limit);
    if (next) {
      setLeaderboard(next);
      setLeaderboardEntries((prev) => [...prev, ...next.rankings]);
    }
    setIsLoadingMoreLeaderboard(false);
  };

  // ---------- Render: Zustände ----------

  if (isLoadingGame) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Spinner color="default" size="lg" />
      </div>
    );
  }

  if (notFound || !game) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-5">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-10 text-center max-w-md">
          <Gamepad2 size={48} className="text-zinc-700 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Tippspiel nicht gefunden</h1>
          <p className="text-gray-400 mb-6">
            Dieses Tippspiel existiert nicht oder ist nicht mehr verfügbar.
          </p>
          <Button
            as={RouterLink}
            to="/tippspiele"
            className="font-semibold text-black"
            style={{ backgroundColor: ACCENT }}
            startContent={<ArrowLeft size={16} />}
          >
            Zurück zu den Tippspielen
          </Button>
        </div>
      </div>
    );
  }

  const statusInfo = predictionService.getGameStatusInfo(game);
  const registrationOpen = predictionService.isRegistrationOpen(game);

  // ---------- Render: Bausteine ----------

  const renderHeader = () => (
    <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border-b border-zinc-800">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-6 md:py-8">
        <RouterLink
          to="/tippspiele"
          className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-4"
        >
          <ChevronLeft size={16} />
          Alle Tippspiele
        </RouterLink>

        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="p-3 rounded-xl flex-shrink-0" style={{ backgroundColor: ACCENT_BG }}>
              <Gamepad2 size={28} style={{ color: ACCENT }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl md:text-2xl font-extrabold text-white">
                  {game.name || 'Tippspiel'}
                </h1>
                <span
                  className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${statusInfo.color} ${statusInfo.bgColor}`}
                >
                  {statusInfo.label}
                </span>
                {game.adminOnly && (
                  <span className="inline-flex items-center rounded-full bg-purple-500/15 px-2.5 py-1 text-xs font-semibold text-purple-400">
                    Nur Admins
                  </span>
                )}
              </div>
              {game.tournament?.name && (
                <p className="text-sm text-gray-400 mt-1 flex items-center gap-1.5">
                  <Trophy size={14} style={{ color: ACCENT }} />
                  {game.tournament.name}
                </p>
              )}
            </div>
          </div>

          {isRegistered && (
            <div className="flex items-center gap-3 flex-shrink-0">
              {participation?.predictionUsername && (
                <span
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold"
                  style={{ color: ACCENT, backgroundColor: ACCENT_BG }}
                >
                  {participation.predictionUsername}
                </span>
              )}
            </div>
          )}
        </div>

        {game.description && <p className="text-gray-400 mt-4 max-w-3xl">{game.description}</p>}

        {/* Game-Stats */}
        <div className="flex items-center gap-5 mt-4 text-sm text-gray-500 flex-wrap">
          <span className="inline-flex items-center gap-1.5">
            <Users size={15} />
            {game.stats?.totalParticipants ?? 0} Teilnehmer
          </span>
          {predictionService.formatGamePeriod(game) && (
            <span className="inline-flex items-center gap-1.5">
              <Clock size={15} />
              {predictionService.formatGamePeriod(game)}
            </span>
          )}
          {game.registrationEnd && (
            <span className="inline-flex items-center gap-1.5">
              <Timer size={15} />
              Anmeldung bis {predictionService.formatDate(game.registrationEnd)}
            </span>
          )}
          {Array.isArray(game.prizes) && game.prizes.length > 0 && (
            <span className="inline-flex items-center gap-1.5" style={{ color: ACCENT }}>
              <Gift size={15} />
              {game.prizes.length} {game.prizes.length === 1 ? 'Preis' : 'Preise'}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const renderLoginHint = () => (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
      <div className="p-2.5 rounded-lg flex-shrink-0" style={{ backgroundColor: ACCENT_BG }}>
        <LogIn size={20} style={{ color: ACCENT }} />
      </div>
      <div className="flex-1">
        <p className="text-white font-semibold">Anmeldung erforderlich</p>
        <p className="text-sm text-gray-400">
          Du musst angemeldet sein, um an diesem Tippspiel teilzunehmen.
        </p>
      </div>
      <Button
        as={RouterLink}
        to="/login"
        size="sm"
        className="font-semibold text-black flex-shrink-0"
        style={{ backgroundColor: ACCENT }}
      >
        Jetzt anmelden
      </Button>
    </div>
  );

  const renderRegistrationCard = () => (
    <div
      className="bg-zinc-900 rounded-2xl border-2 p-6"
      style={{ borderColor: ACCENT_BORDER }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg" style={{ backgroundColor: ACCENT }}>
          <Target size={20} className="text-black" />
        </div>
        <h2 className="text-lg font-bold text-white">Jetzt mitmachen</h2>
      </div>

      {registrationOpen ? (
        <>
          <p className="text-sm text-gray-400 mb-4">
            Wähle einen Tippspiel-Benutzernamen (2–30 Zeichen). Dieser Name erscheint öffentlich
            in der Rangliste.
          </p>
          <div className="max-w-md space-y-4">
            <Input
              label="Tippspiel-Benutzername"
              value={registerUsername}
              onValueChange={setRegisterUsername}
              maxLength={30}
              variant="bordered"
              classNames={{
                inputWrapper: 'border-zinc-700 bg-zinc-950',
                label: 'text-gray-400',
                input: 'text-white',
              }}
            />
            {game.termsUrl && (
              <Checkbox isSelected={acceptedTerms} onValueChange={setAcceptedTerms} size="sm">
                <span className="text-sm text-gray-400">
                  Ich akzeptiere die{' '}
                  <a
                    href={game.termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                    style={{ color: ACCENT }}
                  >
                    Tippspielbedingungen
                  </a>{' '}
                  *
                </span>
              </Checkbox>
            )}
            {registerError && <p className="text-sm text-red-400">{registerError}</p>}
            <Button
              className="font-semibold text-black"
              style={{ backgroundColor: ACCENT }}
              isLoading={isRegistering}
              onClick={handleRegister}
            >
              Anmelden & Mittippen
            </Button>
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-400">
          Die Registrierung für dieses Tippspiel ist geschlossen.
        </p>
      )}
    </div>
  );

  /** Sponsoren-Marquee: läuft direkt unter dem Hero auf allen Tabs außer "Sponsoren". */
  const renderSponsorMarquee = () => {
    if (activeSponsors.length === 0) return null;
    // Inhalt doppelt rendern → nahtloser Endlos-Lauf (Animation bis -50%)
    const items = [...activeSponsors, ...activeSponsors];
    return (
      <div className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900/60 py-3">
        <div className="flex w-max items-center gap-10 animate-dc-marquee hover:[animation-play-state:paused]">
          {items.map((sponsor, idx) => {
            const img = predictionService.buildImageUrl(sponsor.image);
            const content = (
              <span className="flex items-center gap-3 px-2">
                {img && (
                  <img src={img} alt={sponsor.title || 'Sponsor'} className="h-10 max-w-[160px] rounded-lg object-contain" />
                )}
                <span className="whitespace-nowrap text-sm font-semibold text-gray-300">
                  {sponsor.title}
                </span>
              </span>
            );
            return sponsor.linkUrl ? (
              <a
                key={`${sponsor.id || sponsor._id || idx}-${idx}`}
                href={sponsor.linkUrl}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="shrink-0 transition-opacity hover:opacity-80"
              >
                {content}
              </a>
            ) : (
              <span key={`${sponsor.id || sponsor._id || idx}-${idx}`} className="shrink-0">
                {content}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  // ---------- Tab: Sponsoren ----------

  const renderSponsorenTab = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {activeSponsors.map((sponsor, idx) => {
        const img = predictionService.buildImageUrl(sponsor.image);
        return (
          <div
            key={sponsor.id || sponsor._id || idx}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col"
          >
            {img && (
              <div className="flex h-36 items-center justify-center bg-zinc-950/60 p-4">
                <img src={img} alt={sponsor.title || 'Sponsor'} className="max-h-full max-w-full object-contain" />
              </div>
            )}
            <div className="flex flex-1 flex-col p-4">
              <h3 className="text-base font-bold text-white">{sponsor.title}</h3>
              {sponsor.subtitle && <p className="mt-0.5 text-sm text-gray-400">{sponsor.subtitle}</p>}
              {sponsor.description && (
                <p className="mt-2 text-sm text-gray-500">{sponsor.description}</p>
              )}
              {sponsor.linkUrl && (
                <a
                  href={sponsor.linkUrl}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="mt-auto inline-flex items-center gap-1.5 pt-3 text-sm font-semibold"
                  style={{ color: ACCENT }}
                >
                  Mehr erfahren →
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderTabBar = () => {
    const tabs: { key: TabKey; label: string; icon: React.ReactNode; visible: boolean }[] = [
      { key: 'tippen', label: 'Tippen', icon: <Target size={16} />, visible: isRegistered },
      {
        key: 'spezial',
        label: 'Spezial-Tipps',
        icon: <Crown size={16} />,
        visible: isRegistered && hasSpecialPredictions,
      },
      { key: 'meine', label: 'Meine Tipps', icon: <ListChecks size={16} />, visible: isRegistered },
      { key: 'rangliste', label: 'Rangliste', icon: <BarChart3 size={16} />, visible: true },
      { key: 'sponsoren', label: 'Sponsoren', icon: <Gift size={16} />, visible: activeSponsors.length > 0 },
    ];

    return (
      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs
          .filter((tab) => tab.visible)
          .map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                  isActive
                    ? 'text-black'
                    : 'text-gray-400 bg-zinc-900 border border-zinc-800 hover:text-white'
                }`}
                style={isActive ? { backgroundColor: ACCENT } : undefined}
              >
                {tab.icon}
                {tab.label}
              </button>
            );
          })}
      </div>
    );
  };

  // ---------- Tab: Tippen ----------

  const renderMatchCard = (event: PredictionFixture) => {
    const homeName = predictionService.cleanPlayerName(event.homeParticipant?.name);
    const awayName = predictionService.cleanPlayerName(event.awayParticipant?.name);
    const status = (event.status || '').toLowerCase();
    const isFinished = status === 'finished' || status === 'final';
    const isLive = status === 'live';
    const canPredict = predictionService.canPredictMatch(event, game) && isRegistered;
    const deadline = predictionService.getMatchDeadline(event, game);
    const myPrediction = myPredictionForEvent(event);
    const homeScore = event.score?.home?.current;
    const awayScore = event.score?.away?.current;

    return (
      <div
        key={event._id || event.flashliveEventId || `${homeName}-${awayName}`}
        className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 md:p-5"
      >
        {/* Obere Zeile: Runde + Modus + Zeit/Status */}
        <div className="flex items-center justify-between mb-3 text-xs text-gray-500 gap-2">
          <span className="truncate">
            {[event.round, predictionService.formatMatchFormat(event)].filter(Boolean).join(' · ')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            {isLive ? (
              <span className="inline-flex items-center gap-1.5 text-red-400 font-semibold">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
                LIVE
              </span>
            ) : (
              <>
                <Clock size={13} />
                {predictionService.formatDateTime(event.startTime)}
              </>
            )}
          </span>
        </div>

        {/* Spieler-Zeile */}
        <div className="flex items-center gap-3">
          {/* Home */}
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <PlayerAvatar name={homeName} image={event.homeParticipant?.image} />
            <span
              className={`text-sm font-semibold truncate ${
                event.winner === 1 ? '' : 'text-white'
              }`}
              style={event.winner === 1 ? { color: ACCENT } : undefined}
            >
              {homeName || 'TBD'}
            </span>
          </div>

          {/* Score / vs */}
          <div className="flex-shrink-0 text-center px-2">
            {isFinished || isLive ? (
              <span className="text-lg font-extrabold text-white">
                {homeScore ?? '-'}:{awayScore ?? '-'}
              </span>
            ) : (
              <span className="text-sm font-bold text-gray-500">vs</span>
            )}
          </div>

          {/* Away */}
          <div className="flex items-center gap-2.5 flex-1 min-w-0 justify-end">
            <span
              className={`text-sm font-semibold truncate text-right ${
                event.winner === 2 ? '' : 'text-white'
              }`}
              style={event.winner === 2 ? { color: ACCENT } : undefined}
            >
              {awayName || 'TBD'}
            </span>
            <PlayerAvatar name={awayName} image={event.awayParticipant?.image} />
          </div>
        </div>

        {/* Averages bei beendeten Spielen (wie auf /spiele) */}
        {isFinished && (() => {
          const averages = predictionService.getAveragesFromStats(event);
          if (!averages) return null;
          return (
            <div className="mt-2 flex items-center justify-center gap-1.5 text-xs text-gray-500">
              <span className={event.winner === 1 ? 'font-semibold text-gray-300' : ''}>Ø {averages.home}</span>
              <span>·</span>
              <span className={event.winner === 2 ? 'font-semibold text-gray-300' : ''}>Ø {averages.away}</span>
            </div>
          );
        })()}

        {/* Mein Tipp + Aktion */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-800 gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {myPrediction ? (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold"
                style={{ color: ACCENT, backgroundColor: ACCENT_BG }}
              >
                <Check size={13} />
                Dein Tipp:{' '}
                {myPrediction.predictedScore
                  ? `${myPrediction.predictedScore.home ?? '-'}:${myPrediction.predictedScore.away ?? '-'}`
                  : myPrediction.predictedWinner === 1
                  ? `Sieg ${homeName}`
                  : `Sieg ${awayName}`}
              </span>
            ) : (
              <span className="text-xs text-gray-500">Noch kein Tipp abgegeben</span>
            )}
            {myPrediction?.isEvaluated && (
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold ${
                  (myPrediction.points ?? 0) > 0
                    ? 'text-green-400 bg-green-400/10'
                    : 'text-gray-500 bg-zinc-800'
                }`}
              >
                <Star size={12} />
                {predictionService.formatPoints(myPrediction.points)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {canPredict ? (
              <>
                {deadline && (
                  <span className="inline-flex items-center gap-1 text-xs" style={{ color: ACCENT }}>
                    <Timer size={13} />
                    Tipp bis {predictionService.formatTime(deadline.toISOString())}
                  </span>
                )}
                <Button
                  size="sm"
                  className="font-semibold text-black"
                  style={{ backgroundColor: ACCENT }}
                  onClick={() => openPredictionModal(event)}
                >
                  {myPrediction ? 'Tipp ändern' : 'Tippen'}
                </Button>
              </>
            ) : (
              // Nur zeigen, wenn KEIN eigener Tipp existiert — wer getippt hat,
              // sieht links bereits "Dein Tipp: …", der rote Hinweis wäre nur Lärm
              !isFinished &&
              !isLive &&
              !myPrediction && (
                <span className="inline-flex items-center gap-1 text-xs text-red-400">
                  <TimerOff size={13} />
                  Tipp-Deadline abgelaufen
                </span>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTippenTab = () => {
    if (isLoadingEvents) {
      return (
        <div className="flex justify-center py-12">
          <Spinner color="default" />
        </div>
      );
    }
    if (events.length === 0) {
      return (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl py-12 px-5 text-center">
          <Target size={48} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-gray-500">Aktuell sind keine Spiele zum Tippen verfügbar.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Datums-Auswahl */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {sortedDateKeys.map((key) => {
            const isActive = key === selectedDateKey;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedDateKey(key)}
                className={`px-3.5 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                  isActive
                    ? 'text-black'
                    : 'text-gray-400 bg-zinc-900 border border-zinc-800 hover:text-white'
                }`}
                style={isActive ? { backgroundColor: ACCENT } : undefined}
              >
                {predictionService.formatDayLabel(`${key}T12:00:00`)}
              </button>
            );
          })}
        </div>

        {visibleEvents.length === 0 ? (
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl py-10 px-5 text-center">
            <p className="text-gray-500">Keine Spiele an diesem Tag.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {visibleEvents.map((event) => renderMatchCard(event))}
          </div>
        )}
      </div>
    );
  };

  // ---------- Tab: Spezial-Tipps ----------

  const renderPlayerSelectCard = (
    player: TournamentPlayer,
    isSelected: boolean,
    disabled: boolean,
    onClick: () => void
  ) => (
    <button
      key={player.id || player.name || ''}
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center gap-2.5 p-3 rounded-xl border text-left transition-colors ${
        isSelected
          ? 'bg-zinc-900'
          : 'bg-zinc-900 border-zinc-800 hover:border-zinc-600'
      } ${disabled && !isSelected ? 'opacity-50 cursor-not-allowed' : ''}`}
      style={isSelected ? { borderColor: ACCENT, backgroundColor: ACCENT_BG } : undefined}
    >
      <PlayerAvatar name={player.name} image={player.image} size={34} />
      <span className={`text-sm font-medium truncate ${isSelected ? '' : 'text-gray-300'}`} style={isSelected ? { color: ACCENT } : undefined}>
        {predictionService.cleanPlayerName(player.name) || 'Unbekannt'}
      </span>
      {isSelected && <Check size={16} className="ml-auto flex-shrink-0" style={{ color: ACCENT }} />}
    </button>
  );

  const renderSpezialTab = () => {
    if (isLoadingEvents) {
      return (
        <div className="flex justify-center py-12">
          <Spinner color="default" />
        </div>
      );
    }

    const semiFinal = participation?.specialPredictions?.semiFinal ?? null;
    const champion = participation?.specialPredictions?.champion ?? null;
    const deadlineValue = specialDeadline?.semiFinalDeadline || specialDeadline?.autoDeadline || null;

    const filterPlayers = (term: string) => {
      const t = term.trim().toLowerCase();
      if (!t) return players;
      return players.filter((p) =>
        predictionService
          .cleanPlayerName(predictionService.normalizePlayerInfo(p).name)
          .toLowerCase()
          .includes(t)
      );
    };
    const semiPlayers = filterPlayers(semiPlayerSearch);
    const championPlayers = filterPlayers(championPlayerSearch);

    const renderPlayerSearch = (value: string, onChange: (v: string) => void) => (
      <div className="relative mb-3 max-w-sm">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Spieler suchen … (${players.length} im Feld)`}
          className="h-10 w-full rounded-xl border border-zinc-700 bg-zinc-950/60 pl-9 pr-3 text-sm text-white outline-none transition-colors placeholder:text-gray-500 focus:border-[#3fced1]/60"
        />
      </div>
    );

    return (
      <div className="space-y-6">
        {/* Deadline-Banner */}
        <div
          className={`flex items-center gap-3 p-4 rounded-2xl border ${
            canPredictSpecial ? '' : 'border-red-400/30 bg-red-400/5'
          }`}
          style={
            canPredictSpecial
              ? { borderColor: ACCENT_BORDER, backgroundColor: ACCENT_BG }
              : undefined
          }
        >
          {canPredictSpecial ? (
            <Timer size={20} style={{ color: ACCENT }} className="flex-shrink-0" />
          ) : (
            <TimerOff size={20} className="text-red-400 flex-shrink-0" />
          )}
          <div>
            <p
              className={`text-sm font-semibold ${canPredictSpecial ? '' : 'text-red-400'}`}
              style={canPredictSpecial ? { color: ACCENT } : undefined}
            >
              {canPredictSpecial
                ? 'Spezial-Tipps noch möglich'
                : 'Deadline für Spezial-Tipps abgelaufen'}
            </p>
            {deadlineValue && canPredictSpecial && (
              <p className="text-xs text-gray-400 mt-0.5">
                Bis {predictionService.formatDateTime(deadlineValue)} (1h vor Turnierbeginn)
              </p>
            )}
          </div>
        </div>

        {/* Halbfinale */}
        {semiFinalEnabled && (
          <section className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
            <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Award size={18} style={{ color: ACCENT }} />
                Halbfinalisten tippen
              </h3>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ color: ACCENT, backgroundColor: ACCENT_BG }}>
                {predictionService.formatPoints(settings?.specialPredictions?.semiFinalPoints)} pro richtigem Spieler
              </span>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Wähle genau 4 Spieler, die das Halbfinale erreichen ({selectedSemiFinalists.length}/4
              ausgewählt)
            </p>

            {semiFinal?.isEvaluated && (
              <p className="text-sm mb-4 text-green-400">
                Ausgewertet: {semiFinal.correctCount ?? 0} richtig – {predictionService.formatPoints(semiFinal.points)}
              </p>
            )}

            {players.length === 0 ? (
              <p className="text-sm text-gray-500">Keine Spieler verfügbar.</p>
            ) : (
              <>
                {renderPlayerSearch(semiPlayerSearch, setSemiPlayerSearch)}
                {semiPlayers.length === 0 && (
                  <p className="mb-2 text-sm text-gray-500">Kein Spieler passt zur Suche.</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
                  {semiPlayers.map((player) => {
                    const pid = player.id || '';
                    const isSelected = selectedSemiFinalists.includes(pid);
                    const disabled =
                      !canPredictSpecial || (!isSelected && selectedSemiFinalists.length >= 4);
                    return renderPlayerSelectCard(player, isSelected, disabled, () => {
                      if (!canPredictSpecial || !pid) return;
                      toggleSemiFinalist(pid);
                    });
                  })}
                </div>
                {canPredictSpecial && (
                  <Button
                    className="font-semibold text-black mt-4"
                    style={{ backgroundColor: ACCENT }}
                    isDisabled={selectedSemiFinalists.length !== 4}
                    isLoading={isSubmittingSemiFinal}
                    onClick={handleSubmitSemiFinal}
                  >
                    Halbfinale-Tipp speichern
                  </Button>
                )}
              </>
            )}
          </section>
        )}

        {/* Champion */}
        {championEnabled && (
          <section className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
            <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Crown size={18} style={{ color: ACCENT }} />
                Turniersieger tippen
              </h3>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ color: ACCENT, backgroundColor: ACCENT_BG }}>
                {predictionService.formatPoints(settings?.specialPredictions?.championPoints)}
              </span>
            </div>
            <p className="text-sm text-gray-400 mb-4">Wer gewinnt das Turnier?</p>

            {champion?.isEvaluated && (
              <p className={`text-sm mb-4 ${champion.isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                {champion.isCorrect
                  ? `Richtig getippt! ${predictionService.formatPoints(champion.points)}`
                  : 'Leider falsch getippt'}
              </p>
            )}

            {players.length === 0 ? (
              <p className="text-sm text-gray-500">Keine Spieler verfügbar.</p>
            ) : (
              <>
                {renderPlayerSearch(championPlayerSearch, setChampionPlayerSearch)}
                {championPlayers.length === 0 && (
                  <p className="mb-2 text-sm text-gray-500">Kein Spieler passt zur Suche.</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
                  {championPlayers.map((player) => {
                    const pid = player.id || '';
                    const isSelected = selectedChampion === pid;
                    return renderPlayerSelectCard(player, isSelected, !canPredictSpecial, () => {
                      if (!canPredictSpecial || !pid) return;
                      setSelectedChampion(pid);
                    });
                  })}
                </div>
                {canPredictSpecial && (
                  <Button
                    className="font-semibold text-black mt-4"
                    style={{ backgroundColor: ACCENT }}
                    isDisabled={!selectedChampion}
                    isLoading={isSubmittingChampion}
                    onClick={handleSubmitChampion}
                  >
                    Sieger-Tipp speichern
                  </Button>
                )}
              </>
            )}
          </section>
        )}
      </div>
    );
  };

  // ---------- Tab: Meine Tipps ----------

  const renderMeineTab = () => {
    const predictions = [...(participation?.matchPredictions ?? [])].sort((a, b) => {
      const ta = predictionService.parseDate(a.submittedAt)?.getTime() ?? 0;
      const tb = predictionService.parseDate(b.submittedAt)?.getTime() ?? 0;
      return tb - ta;
    });

    const statCards = [
      { label: 'Punkte', value: myStats?.totalPoints ?? participation?.totalPoints ?? 0, icon: <Star size={18} /> },
      { label: 'Rang', value: myStats?.rank ?? participation?.rank ?? '–', icon: <BarChart3 size={18} /> },
      { label: 'Tipps', value: myStats?.matchPredictions?.total ?? predictions.length, icon: <Target size={18} /> },
      { label: 'Richtig', value: myStats?.matchPredictions?.correct ?? 0, icon: <Check size={18} /> },
      { label: 'Exakt', value: myStats?.matchPredictions?.exactScores ?? 0, icon: <Award size={18} /> },
    ];

    const semiFinal = participation?.specialPredictions?.semiFinal ?? null;
    const champion = participation?.specialPredictions?.champion ?? null;

    return (
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {statCards.map((card) => (
            <div key={card.label} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 text-center">
              <div className="flex justify-center mb-1.5" style={{ color: ACCENT }}>
                {card.icon}
              </div>
              <p className="text-xl font-extrabold text-white">{card.value}</p>
              <p className="text-xs text-gray-500">{card.label}</p>
            </div>
          ))}
        </div>

        {/* Spezial-Tipps Übersicht */}
        {hasSpecialPredictions && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {semiFinalEnabled && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
                <p className="text-sm font-semibold text-white flex items-center gap-2 mb-2">
                  <Award size={15} style={{ color: ACCENT }} />
                  Halbfinale-Tipp
                </p>
                {semiFinal && (semiFinal.players?.length ?? 0) > 0 ? (
                  <p className="text-sm text-gray-400">
                    {(semiFinal.players ?? [])
                      .map((p) => predictionService.cleanPlayerName(predictionService.normalizePlayerInfo(p).name))
                      .filter(Boolean)
                      .join(', ')}
                    {semiFinal.isEvaluated && (
                      <span className="block mt-1 text-green-400">
                        {semiFinal.correctCount ?? 0} richtig – {predictionService.formatPoints(semiFinal.points)}
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">Noch nicht abgegeben</p>
                )}
              </div>
            )}
            {championEnabled && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
                <p className="text-sm font-semibold text-white flex items-center gap-2 mb-2">
                  <Crown size={15} style={{ color: ACCENT }} />
                  Sieger-Tipp
                </p>
                {champion?.playerId ? (
                  <p className="text-sm text-gray-400">
                    {predictionService.cleanPlayerName(champion.playerName) || 'Unbekannt'}
                    {champion.isEvaluated && (
                      <span className={`block mt-1 ${champion.isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                        {champion.isCorrect ? `Richtig – ${predictionService.formatPoints(champion.points)}` : 'Falsch getippt'}
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">Noch nicht abgegeben</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Match-Tipps Liste */}
        <section className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-zinc-800">
            <h3 className="text-lg font-bold text-white">Meine Match-Tipps</h3>
          </div>
          {predictions.length === 0 ? (
            <div className="py-10 px-5 text-center">
              <ListChecks size={40} className="text-zinc-700 mx-auto mb-3" />
              <p className="text-gray-500">Du hast noch keine Tipps abgegeben.</p>
            </div>
          ) : (
            <div>
              {predictions.map((prediction, index) => {
                const home = predictionService.normalizePlayerInfo(prediction.homePlayer);
                const away = predictionService.normalizePlayerInfo(prediction.awayPlayer);
                const tipText = prediction.predictedScore
                  ? `${prediction.predictedScore.home ?? '-'}:${prediction.predictedScore.away ?? '-'}`
                  : prediction.predictedWinner === 1
                  ? `Sieg ${predictionService.cleanPlayerName(home.name)}`
                  : `Sieg ${predictionService.cleanPlayerName(away.name)}`;
                const resultText = prediction.actualScore
                  ? `${prediction.actualScore.home ?? '-'}:${prediction.actualScore.away ?? '-'}`
                  : null;

                return (
                  <div
                    key={`${prediction.flashliveEventId || index}`}
                    className="px-5 py-4 border-b border-zinc-800 last:border-b-0 flex items-center gap-4 flex-wrap"
                  >
                    <div className="flex-1 min-w-[200px]">
                      <p className="text-sm font-semibold text-white">
                        {predictionService.cleanPlayerName(home.name) || 'TBD'} vs{' '}
                        {predictionService.cleanPlayerName(away.name) || 'TBD'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {prediction.round ? `${prediction.round} · ` : ''}
                        Dein Tipp: <span style={{ color: ACCENT }}>{tipText}</span>
                        {resultText && <> · Ergebnis: {resultText}</>}
                      </p>
                    </div>
                    {prediction.isEvaluated ? (
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold flex-shrink-0 ${
                          (prediction.points ?? 0) > 0
                            ? 'text-green-400 bg-green-400/10'
                            : 'text-gray-500 bg-zinc-800'
                        }`}
                      >
                        <Star size={12} />
                        {predictionService.formatPoints(prediction.points)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-gray-400 bg-zinc-800 flex-shrink-0">
                        <Clock size={12} />
                        Offen
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    );
  };

  // ---------- Tab: Rangliste ----------

  const renderRanglisteTab = () => {
    if (isLoadingLeaderboard) {
      return (
        <div className="flex justify-center py-12">
          <Spinner color="default" />
        </div>
      );
    }

    if (!leaderboard || leaderboardEntries.length === 0) {
      return (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl py-12 px-5 text-center">
          <BarChart3 size={48} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-gray-500">Noch keine Einträge in der Rangliste.</p>
        </div>
      );
    }

    const myUsername = participation?.predictionUsername ?? null;
    const hasMore = leaderboard.page < leaderboard.totalPages;

    return (
      <section className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Rangliste</h3>
          <span className="text-sm text-gray-500">{leaderboard.totalEntries} Teilnehmer</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-zinc-800">
                <th className="px-5 py-3 font-semibold">Platz</th>
                <th className="px-5 py-3 font-semibold">Name</th>
                <th className="px-5 py-3 font-semibold text-right">Tipps</th>
                <th className="px-5 py-3 font-semibold text-right">Richtig</th>
                <th className="px-5 py-3 font-semibold text-right">Punkte</th>
              </tr>
            </thead>
            <tbody>
              {leaderboardEntries.map((entry, index) => {
                const isMe =
                  myUsername !== null &&
                  entry.predictionUsername !== null &&
                  entry.predictionUsername === myUsername;
                const rank = entry.rank ?? index + 1;
                return (
                  <tr
                    key={`${entry.predictionUsername || index}-${rank}`}
                    className={`border-b border-zinc-800/60 last:border-b-0 ${
                      isMe ? '' : 'hover:bg-zinc-800/40'
                    }`}
                    style={isMe ? { backgroundColor: ACCENT_BG } : undefined}
                  >
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${
                          rank === 1
                            ? 'bg-yellow-400/15 text-yellow-400'
                            : rank === 2
                            ? 'bg-gray-300/15 text-gray-300'
                            : rank === 3
                            ? 'bg-amber-600/15 text-amber-500'
                            : 'bg-zinc-800 text-gray-400'
                        }`}
                      >
                        {rank}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`font-semibold ${isMe ? '' : 'text-white'}`} style={isMe ? { color: ACCENT } : undefined}>
                        {entry.predictionUsername || 'Teilnehmer'}
                        {isMe && <span className="ml-2 text-xs">(Du)</span>}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-400">
                      {entry.matchPredictions?.total ?? 0}
                    </td>
                    <td className="px-5 py-3 text-right text-gray-400">
                      {entry.matchPredictions?.correct ?? 0}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span className="font-bold" style={{ color: ACCENT }}>
                        {entry.totalPoints ?? 0}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div className="p-4 flex justify-center border-t border-zinc-800">
            <Button
              variant="bordered"
              className="border-zinc-700 text-gray-300"
              isLoading={isLoadingMoreLeaderboard}
              onClick={loadMoreLeaderboard}
            >
              Mehr anzeigen
            </Button>
          </div>
        )}
      </section>
    );
  };

  // ---------- Render ----------

  const pageTitle = `${game.name || 'Tippspiel'} | Tippspiele | DartCloud`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={
            game.description ||
            `Tippe beim Tippspiel "${game.name || ''}" auf Darts-Ergebnisse und gewinne Preise.`
          }
        />
        {game.slug && <link rel="canonical" href={`https://dartcloudapp.de/tippspiele/${game.slug}`} />}
        <meta property="og:title" content={pageTitle} />
        {game.description && <meta property="og:description" content={game.description} />}
      </Helmet>

      <div className="min-h-screen bg-black">
        {renderHeader()}

        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 space-y-6">
          {/* Sponsoren-Marquee: direkt unter dem Hero, auf allen Tabs außer "Sponsoren" */}
          {activeTab !== 'sponsoren' && renderSponsorMarquee()}

          {/* Feedback-Banner */}
          {feedback && (
            <div
              className={`flex items-center gap-2.5 p-4 rounded-2xl border text-sm font-semibold ${
                feedback.type === 'success'
                  ? 'text-green-400 border-green-400/30 bg-green-400/5'
                  : 'text-red-400 border-red-400/30 bg-red-400/5'
              }`}
            >
              {feedback.type === 'success' ? <Check size={16} /> : <X size={16} />}
              {feedback.text}
            </div>
          )}

          {/* Nicht eingeloggt → Login-Hinweis | eingeloggt + nicht registriert → Formular */}
          {!isAuthenticated && renderLoginHint()}
          {isAuthenticated && !isRegistered && renderRegistrationCard()}

          {/* Tabs */}
          {renderTabBar()}

          {activeTab === 'tippen' && isRegistered && renderTippenTab()}
          {activeTab === 'spezial' && isRegistered && hasSpecialPredictions && renderSpezialTab()}
          {activeTab === 'meine' && isRegistered && renderMeineTab()}
          {activeTab === 'rangliste' && renderRanglisteTab()}
          {activeTab === 'sponsoren' && renderSponsorenTab()}
        </div>
      </div>

      {/* Tipp-Modal */}
      <Modal
        isOpen={predictionModalFixture !== null}
        onClose={() => setPredictionModalFixture(null)}
        classNames={{
          base: 'bg-zinc-900 border border-zinc-800',
          header: 'text-white',
          closeButton: 'text-gray-400',
        }}
      >
        <ModalContent>
          <ModalHeader>Tipp abgeben</ModalHeader>
          <ModalBody>
            {predictionModalFixture && (
              <>
                <p className="text-center text-white font-semibold">
                  {predictionService.cleanPlayerName(predictionModalFixture.homeParticipant?.name) || 'TBD'}{' '}
                  vs{' '}
                  {predictionService.cleanPlayerName(predictionModalFixture.awayParticipant?.name) || 'TBD'}
                </p>
                {predictionService.formatMatchFormat(predictionModalFixture) && (
                  <p className="text-center text-xs text-gray-500">
                    {predictionService.formatMatchFormat(predictionModalFixture)}
                    {predictionService.getWinningLegs(predictionModalFixture) != null && (
                      <> · Der Sieger braucht genau {predictionService.getWinningLegs(predictionModalFixture)} Legs</>
                    )}
                  </p>
                )}
                <div className="flex items-center gap-4 mt-2">
                  <Input
                    type="number"
                    min={0}
                    label={
                      predictionService
                        .cleanPlayerName(predictionModalFixture.homeParticipant?.name)
                        .split(' ')
                        .pop() || 'Heim'
                    }
                    value={modalHomeScore}
                    onValueChange={setModalHomeScore}
                    variant="bordered"
                    classNames={{
                      inputWrapper: 'border-zinc-700 bg-zinc-950',
                      label: 'text-gray-400',
                      input: 'text-white text-center text-xl font-bold',
                    }}
                  />
                  <span className="text-2xl font-extrabold text-gray-500">:</span>
                  <Input
                    type="number"
                    min={0}
                    label={
                      predictionService
                        .cleanPlayerName(predictionModalFixture.awayParticipant?.name)
                        .split(' ')
                        .pop() || 'Gast'
                    }
                    value={modalAwayScore}
                    onValueChange={setModalAwayScore}
                    variant="bordered"
                    classNames={{
                      inputWrapper: 'border-zinc-700 bg-zinc-950',
                      label: 'text-gray-400',
                      input: 'text-white text-center text-xl font-bold',
                    }}
                  />
                </div>
                {modalError && <p className="text-sm text-red-400 text-center">{modalError}</p>}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="bordered"
              className="border-zinc-700 text-gray-300"
              onClick={() => setPredictionModalFixture(null)}
            >
              Abbrechen
            </Button>
            <Button
              className="font-semibold text-black"
              style={{ backgroundColor: ACCENT }}
              isLoading={isSubmittingPrediction}
              onClick={handleSubmitPrediction}
            >
              Speichern
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PredictionGameDetailPage;
